/**
 * This component provides a color picker
 *
 * @author Afifa Saleem <afifa@250mils.com>
 */

import React from 'react';
import './color-picker.scss';
import {HexColorPicker} from "@postermywall/react-colorful";
import Tooltip from "react-bootstrap/Tooltip";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import PropTypes from "prop-types";
import {Dropdown} from "../dropdown";
import {CanvasColorPicker} from "../canvas-color-picker";
import {colorToHex, isCorrectHexFormat, getFormattedHexColor} from '@Utils/color.util'
import {EditorBrandsContainer} from "../editor-brands-container";
import {BrandColors} from "../editor-brands-container/components/brand-colors";
import {ColorSwatch} from "./components/color-swatch";
import {arraysEqual} from "@Utils/array.util";
import {copyToClipboard} from "@Utils/clipboard.util";
import {EyedropperColorPicker} from "@Components/eyedropper-color-picker/eyedropper-color-picker";
import {trackHalloweenBrandKitUsageIfApplicable} from "@Libraries/brands-library.js";

const COLOR_FILL_TYPE = {
    SOLID: 0,
    GRADIENT: 1
};

export default class ColorPicker extends React.Component {
    /**
     * @type {Array}
     */
    #colorTypeOptions = [];
    /**
     * Used to store the previous color for undo use cases
     * @type {string}
     */
    #initialColor = '';

    constructor(props) {
        super(props);
        this.inputRef = React.createRef();
        this.state = {
            colors: props.initialColors,
            showPickColorFromPage: props.showPickColorFromPage,
            showGeneralColorPickerFromPage: props.showGeneralColorPickerFromPage,
            showMessage: false,
            selectedColor: 0,
            type: props.initialType,
            hexInput: props.initialColors[0]
        };
    }

    /**
     * handle changes in different props and update the state accordingly
     * @param prevProps
     * @param prevState
     * @param snapshot
     */
    componentDidUpdate(prevProps, prevState, snapshot) {
        if (this.props.showPickColorFromPage !== prevProps.showPickColorFromPage) {
            this.setState({
                showPickColorFromPage: this.props.showPickColorFromPage
            });
            this.#onNewShowPickColorProp();
        }
        if (this.props.showGeneralColorPickerFromPage !== prevProps.showGeneralColorPickerFromPage) {
            this.setState({
                showGeneralColorPickerFromPage: this.props.showGeneralColorPickerFromPage
            });
        }
        if (!arraysEqual(this.props.initialColors, prevProps.initialColors) && !arraysEqual(this.props.initialColors, this.state.colors)) {
            this.#onNewColorProps();
        }

        if (this.props.initialType !== prevProps.initialType) {
            this.#onNewInitialTypeProp();
        }
        if (prevState.selectedColor !== this.state.selectedColor) {
            this.#setHexInput(this.state.colors[this.state.selectedColor]);
        }

        if (!prevProps.clearHexInput && this.props.clearHexInput) {
            this.#setHexInput(this.props.initialColors[this.state.selectedColor]);
        }
    }

    /**
     * Render the component
     * @return {JSX.Element}
     */
    render = () => {
        return <div className={'sections inline-options'}>
            {this.#getDropDownMarkup()}
            {this.#getColorSelectors()}
            {this.#getColorPicker()}
        </div>
    };

    /**
     * @return {JSX.Element}
     */
    #getDropDownMarkup = () => {
        if (this.props.enableGradient) {
            return (
                <div className={'fill-type'}>
                    <label>{i18next.t('pmwjs_type')}</label>
                    <button className={'color-fill-type'}>
                        <Dropdown
                            dropdownItemClasses={'body-md-regular'}
                            dropdownItems={this.#getDropDownOptions()}
                            clickHandler={this.#updateColorType}
                            defaultLabel={this.#colorTypeOptions[this.props.initialType].label}
                            defaultDataValue={this.#colorTypeOptions[this.props.initialType].dataValue}
                            dropdownLabelClasses={'-custom-lbl'}
                            dropdownContentClasses={'-width-uncapped'}
                        />
                    </button>
                </div>
            );
        }
        return null;
    };

    /**
     * @return {JSX.Element}
     */
    #getColorSelectors = () => {
        return (
            <div className={'color-display-container'}>
                <div className={'solidColorFill ' + (this.state.type !== COLOR_FILL_TYPE.SOLID ? '_hidden' : '-selected')}>
                    <label className={'-custom-lbl'}>{i18next.t('pmwjs_color')}</label>
                    <span className="color-fill-display" style={{backgroundColor: `#${this.state.colors[0]}`}}/>
                </div>
                {this.#getGradientColorsMarkup()}
            </div>
        );
    };

    /**
     * @return {JSX.Element}
     */
    #getColorPicker = () => {
        return (
            <div className={`color-picker-container ${this.props.classes}`}>
                {this.#getSaturationComponent()}
                {this.#getInputContainer()}
                {this.#getBrandColorsComponent()}
                {this.#getColorSwatchesMarkup()}
            </div>
        );
    };

    /**
     * @return {JSX.Element}
     */
    #getSaturationComponent = () => {
        return <div className='saturation'>
            <HexColorPicker
                color={this.state.colors[this.state.selectedColor]}
                onChange={this.#onColorChanging}
                onPointerDown={this.#beforeColorChanging}
                onPointerUp={this.#onColorChanged}
            />
        </div>
    };

    /**
     * @return {JSX.Element}
     */
    #getInputContainer = () => {
        return <div className='color-input-container'>
            <div className={`hex-input-container ${this.props.colorInputContainerClasses}`}>
                <div className={`hex-input ${this.props.inputColorSwatch ? '-swatch-visible' : ''}`}>
                    <div className={'color-swatch ' + (!this.props.inputColorSwatch ? '_hidden' : '')}
                         style={{background: '#' + this.state.colors[this.state.selectedColor]}}
                    />
                    <span className='hex-symbol'>#</span>
                    <input
                        className={'hex-input-field'}
                        ref={this.inputRef}
                        value={this.state.hexInput}
                        maxLength={6}
                        onFocus={this.#hexInputFieldFocus}
                        onBlur={this.#onHexInputFieldBlur}
                        onChange={this.#onHexInputChanged}
                    />
                    {this.#getCopyColorOverlayMarkup()}
                </div>
            </div>
            {this.#getPickColorFromPageButton()}
        </div>
    };

    /**
     * @return {JSX.Element}
     */
    #getColorSwatchesMarkup = () => {
        if (this.props.swatchColors) {
            const swatchesHeading = this.props.swatchesHeading || i18next.t('pmwjs_on_my_design');

            return <div className={'color-swatch-container body-md-bold'}>
                <h4 className={'heading body-md-bold'}>{swatchesHeading}</h4>
                {this.#getColorSwatches()}
            </div>
        }
    };

    /**
     * @return {JSX.Element}
     */
    #getColorSwatches = () => {
        let colors = this.props.swatchColors,
            swatches = [];
        for (let i in colors) {
            if (colors.hasOwnProperty(i)) {
                swatches.push(
                    <ColorSwatch
                        key={`swatch-${i}-${colors[i]}`}
                        color={colors[i]}
                        onClick={this.#handleSwatchColorChange}
                    />
                );
            }
        }
        return (<div className="color-swatches">{swatches}</div>);
    };

    #getBrandColorsComponent = () => {
        if (this.props.showBrandColors) {
            return (
                <EditorBrandsContainer brandsHeaderTextClass={'body-md-bold'}>
                    <BrandColors
                        onColorSelect={this.#handleSwatchColorChange}
                        onAddBrandAsset={this.props.onAddBrandAsset}
                    />
                </EditorBrandsContainer>
            );
        }
    };

    /**
     * @return {JSX.Element}
     */
    #getPickColorFromPageUsingBrowserAPIButton = () => {
        if (this.state.showPickColorFromPage) {
            return <div className='canvas-color-picker'>
                <i className='icon icon-colorpicker'/>
                <span className={'text-color-picker'}>{i18next.t('pmwjs_picker')}</span>
                <CanvasColorPicker
                    canvas={this.props.canvas}
                    canvasContainer={this.props.canvasContainer}
                    scale={this.props.scale}
                    beforeColorPicking={this.#beforeColorPicking}
                    onColorPicking={this.#onColorChanging}
                    onColorPicked={this.#onColorChanged}
                    afterColorPicked={this.props.afterColorPicked}
                    onColorPickingRevert={this.#onColorPickingRevert}
                />
            </div>;
        }
    };

    /**
     * @return {JSX.Element}
     */
    #getPickColorFromPageButton = () => {
        if (this.state.showPickColorFromPage) {
            return <div className='canvas-color-picker'>
                <i className='icon icon-colorpicker'/>
                <span className={'text-color-picker'}>{i18next.t('pmwjs_picker')}</span>
                <CanvasColorPicker
                    canvas={this.props.canvas}
                    canvasContainer={this.props.canvasContainer}
                    scale={this.props.scale}
                    beforeColorPicking={this.#beforeColorPicking}
                    onColorPicking={this.#onColorChanging}
                    onColorPicked={this.#onColorChanged}
                    afterColorPicked={this.props.afterColorPicked}
                    onColorPickingRevert={this.#onColorPickingRevert}
                />
            </div>;
        }

        if (this.state.showGeneralColorPickerFromPage) {
            return <EyedropperColorPicker
                customClasses="spacing-m-l-2"
                onColorPicked={this.#onGeneralPickerColorChanged}
            />
        }
    };

    /**
     * @return {JSX.Element}
     */
    #getCopyColorOverlayMarkup = () => {
        return (
            <OverlayTrigger
                {...this.props}
                trigger="click"
                placement="top"
                delay={{show: 200, hide: 100}}
                show={this.state.showMessage}
                overlay={
                    <Tooltip
                        id="colorpicker-hex-copy"
                    >
                        {i18next.t('pmwjs_insights_keywords_copied')}
                    </Tooltip>
                }
            >
                <i className={'icon icon-copy copy-color-fill'}
                   onClick={this.#toggleToolTip}/>
            </OverlayTrigger>
        );
    };


    /**
     * Hide tooltip within 2 seconds after displaying
     */
    #toggleToolTip = async (e) => {
        let target = e.currentTarget;
        $(target).addClass('-copied');

        this.setState({
            showMessage: true
        }, () => {
            setTimeout(() => {
                this.setState({
                    showMessage: false
                });
                $(target).removeClass('-copied')
            }, 1500);
        });

        await copyToClipboard($(this.inputRef.current).val());
    };

    /**
     * @return {JSX.Element}
     */
    #getGradientColorsMarkup = () => {
        if (this.props.enableGradient) {
            let colors = this.state.colors,
                displayColors = [];
            for (let i = 0; i < colors.length; i++) {
                if (colors.hasOwnProperty(i)) {
                    displayColors.push(<div className={'gradientColorFill gradient-color' + i + ' ' + (this.state.type !== COLOR_FILL_TYPE.SOLID && this.state.selectedColor === i ? '-selected' : '')}
                                            key={`gradient-${i}`}
                                            onClick={this.#gradientSelectionUpdated.bind(null, i)}>
                        <input type="radio"
                               readOnly={true}
                               value={i}
                               checked={this.state.selectedColor === i}/>
                        <label className={'-custom-lbl'}>{i18next.t('pmwjs_color_' + (i + 1))}</label>
                        <span className="color-fill-display"
                              style={{backgroundColor: `#${this.state.colors[i]}`}}/>
                    </div>);
                }
            }
            return (<div className={'gradient-color-container ' + (this.state.type === COLOR_FILL_TYPE.SOLID ? '_hidden' : '')}>{displayColors}</div>);
        }
    };

    /**
     *
     */
    #getDropDownOptions = () => {
        this.#colorTypeOptions = [{
            label: 'Solid',
            dataValue: '0'
        },
            {
                label: 'Linear',
                dataValue: '1'
            },
            {
                label: 'Radial',
                dataValue: '2'
            }];
        return this.#colorTypeOptions;
    };

    /**
     * @param {string} val
     */
    #updateColorType = (val) => {
        if (parseInt(val) !== this.state.type) {
            this.setState({
                type: parseInt(val),
                selectedColor: 0
            });
            this.props.onColorTypeChanged(val);
        }
    };

    /**
     * @param {number} value
     */
    #gradientSelectionUpdated = (value) => {
        if (this.state.selectedColor !== value) {
            this.setState({
                selectedColor: value
            })
        }
    };

    #onGeneralPickerColorChanged = (color) => {
        color = colorToHex(color);
        this.props.onColorChanged(color, this.#initialColor);
        this.props.afterColorPicked(color, this.#initialColor);
    }

    /**
     * Handler for after the color is changed and mouse up is fired
     */
    #onColorChanged = () => {
        this.#setHexInput(this.state.colors[this.state.selectedColor]);
        this.props.onColorChanged(this.state.colors[this.state.selectedColor], this.#initialColor);
    };

    /**
     * Handler for when the color is changing (on mouse move)
     * @param color
     */
    #onColorChanging = (color) => {
        color = colorToHex(color);
        this.#setHexInput(color);
        this.#handleColorChange(color);
        if (typeof this.props.onColorChanging === 'function') {
            this.props.onColorChanging(color);
        }
    };

    /**
     * Fired before color is changed to save the last color for undo stack
     */
    #beforeColorChanging = () => {
        this.#initialColor = this.state.colors[this.state.selectedColor];
        document.addEventListener('pointerup', this.#handleDocumentClick);
    };

    /**
     * Fired before color is changed to save the last color for undo stack
     */
    #beforeColorPicking = () => {
        this.#beforeColorChanging();
        this.props.beforeColorPicking();
    };

    /**
     * Revert color to what it was before entering color pick mode
     */
    #onColorPickingRevert = () => {
        this.#onColorChanging(this.#initialColor);
    };

    /**
     * Handler for when color is changed from color panel
     * @param color
     */
    #handleColorChange = (color) => {
        let colors = [...this.state.colors];
        colors[this.state.selectedColor] = color;
        this.setState({
            colors: colors,
        });
    };

    /**
     * Handler for when color is changed from color panel
     * @param color
     */
    #handleSwatchColorChange = (color) => {
        trackHalloweenBrandKitUsageIfApplicable();
        color = colorToHex(color);
        this.props.onColorChanged(color, this.state.colors[this.state.selectedColor]);
        this.#setHexInput(color);
        this.#handleColorChange(color);
    };

    /**
     * Handler for when hex input value is changed from color panel
     * the Hex Input value is set to whatever the user is typing. the value is kept lowercase as we can receive
     * caps lock input too, but we stay consistent with the format (lowercase) our color library gives.
     * @param e
     */
    #onHexInputChanged = (e) => {
        let color = e.target.value ?? '';
        color = color.toLowerCase();
        this.#setHexInput(color);
        color = isCorrectHexFormat(color) ? color : getFormattedHexColor(color);
        if (color !== this.state.colors[this.state.selectedColor]) {
            this.props.onColorChanged(color, this.state.colors[this.state.selectedColor]);
        }
        this.#handleColorChange(color);
    };

    /**
     * If the input field is empty on blur, change the hexInput value to the current selected color in state
     * @param e
     */
    #onHexInputFieldBlur = (e) => {
        if (!e.target.value) {
            this.#setHexInput(this.state.colors[this.state.selectedColor]);
        }
    };

    /**
     * select all hex input values on input element focus
     * @param e
     */
    #hexInputFieldFocus = (e) => {
        if (e.target.selectionStart !== e.target.selectionEnd) {
            e.target.setSelectionRange(0, 0);
        }
        e.target.select();
    };

    /**
     *
     * @param {string} color
     */

    #setHexInput = (color) => {
        this.setState({
            hexInput: color
        })
    }

    /**
     * update the state with the latest color information receieved via props
     */
    #onNewColorProps = () => {
        this.setState({
            colors: this.props.initialColors,
            hexInput: this.props.initialColors[this.state.selectedColor]
        });
    }

    /**
     * update the state with latest showPickColorFromPage prop
     */
    #onNewShowPickColorProp = () => {
        this.setState({
            showPickColorFromPage: this.props.showPickColorFromPage
        });
    }

    /**
     * Update the relevant state when new initialType prop is passed
     * Reset the selectedColor to 0 when changing between color types because a selected color is only valid for a current color type (solid, gradient etc)
     */
    #onNewInitialTypeProp = () => {
        this.setState({
            type: this.props.initialType,
            selectedColor: 0
        });
    }

    /**
     * Returns true if color is clicked from outside the picker
     * @param {Event} e
     * @returns {boolean}
     */
    #isClickedOutsideColorPicker = (e) => {
        return !$(e).hasClass('react-colorful') && !$(e).parents().hasClass('react-colorful')
    }

    /**
     * handler if click came from outside the color picker
     * @param {Event} e
     */
    #handleDocumentClick = (e) => {
        if (this.#isClickedOutsideColorPicker(e.target)) {
            this.#onColorChanged()
        }
        document.removeEventListener('pointerup', this.#handleDocumentClick);
    }
}

ColorPicker.propTypes = {
    /**
     * An array of hex colors from 0 to n values.
     */
    initialColors: PropTypes.array.isRequired,

    /**
     * optional param that is used for type of color
     */
    initialType: PropTypes.number,

    /**
     * optional param to show multiple colors
     */
    enableGradient: PropTypes.bool,

    /**
     * optional bool that is used to add pick from design option
     */
    showPickColorFromPage: PropTypes.bool,
    /**
     * Optional bool that is used to add pick from design option using the browser's eyedrop API
     */
    showGeneralColorPickerFromPage: PropTypes.bool,

    /**
     * Heading text for displaying color swatches on current design
     */
    swatchesHeading: PropTypes.string,

    /**
     * optional array that contains colors in hex to display
     */
    swatchColors: PropTypes.array,

    /**
     * optional bool that is used to add a color swatch in hex input field
     */
    inputColorSwatch: PropTypes.bool,

    /**
     * a handler for when color type is updated
     */
    onColorTypeChanged: PropTypes.func,

    /**
     * a handler that invokes when the color is updated, passes new and old color in params
     */
    onColorChanged: PropTypes.func.isRequired,

    /**
     * a handler that invokes when the color is being updated
     */
    onColorChanging: PropTypes.func,

    /**
     * a handler that invokes after the color is picked from page
     */
    afterColorPicked: PropTypes.func,

    /**
     * a handler that invokes before the color is picked from page
     */
    beforeColorPicking: PropTypes.func,

    /**
     * a handler that invokes for adding a brand asset
     */
    onAddBrandAsset: PropTypes.func,

    /**
     * canvas object from which color is to be picked in color picked mode
     * Required when showPickColorFromPage is true
     */
    canvas: PropTypes.object,

    /**
     * canvas object container
     * Required when showPickColorFromPage is true
     */
    canvasContainer: PropTypes.object,

    /**
     * scale of canvas
     * Required when showPickColorFromPage is true
     */
    scale: PropTypes.number,

    /**
     * clear hex input field to be set to new color hex
     */
    clearHexInput: PropTypes.bool,

    /**
     * to show brand colors in color panel
     */
    showBrandColors: PropTypes.bool,

    /**
     * classes to give to the root container
     */
    classes: PropTypes.string,

    /**
     * classes to give to the hex color input container
     */
    colorInputContainerClasses: PropTypes.string
};

ColorPicker.defaultProps = {
    inputColorSwatch: false,
    clearHexInput: false,
    showBrandColors: false,
    classes: '',
    colorInputContainerClasses: '',
    onAddBrandAsset: $.noop(),
    beforeColorPicking: $.noop(),
    onColorChanging: $.noop(),
    showGeneralColorPickerFromPage: false
}
